import { useState } from 'react';
import {
  type SubItemForm,
  type LaborSubItemForm,
  type DirectSubItemForm,
  isQtyRateType,
  getSubItemLabel,
} from './subItemTypes';
import { getCostBreakdown, DEFAULT_TAX_RATE, DEFAULT_MARKUP_RATE } from '@jobos/shared';
import { LaborSubItemRow } from './LaborSubItemRow';
import { DirectSubItemRow } from './DirectSubItemRow';
import { CostBreakdownSummary } from './CostBreakdownSummary';
import { useSubItemCategoriesContext, useSubItemTypeMeta } from '@/contexts/SubItemCategoriesContext';

interface SubItemEditorProps {
  subItems: SubItemForm[];
  onSubItemsChange: (subItems: SubItemForm[]) => void;
  readOnly?: boolean;
  taxRate?: number;
  markupRate?: number;
  roundingPrecision?: number | null;
  onRemoveRounding?: () => void;
  selectedSubItemId?: string | null;
  onSelectSubItem?: (id: string) => void;
}

export function SubItemEditor({ subItems, onSubItemsChange, readOnly = false, taxRate = DEFAULT_TAX_RATE, markupRate = DEFAULT_MARKUP_RATE, roundingPrecision, onRemoveRounding, selectedSubItemId, onSelectSubItem }: SubItemEditorProps) {
  const { categories } = useSubItemCategoriesContext();
  const typeMeta = useSubItemTypeMeta();
  const breakdown = getCostBreakdown(subItems, { taxRate, markupRate }, typeMeta, categories, roundingPrecision);
  const [editingNoteIds, setEditingNoteIds] = useState<Set<string>>(new Set());

  const startEditingNote = (id: string) => {
    setEditingNoteIds((prev) => new Set([...prev, id]));
  };

  const stopEditingNote = (id: string) => {
    setEditingNoteIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleRemoveSubItem = (id: string) => {
    onSubItemsChange(subItems.filter((item) => item.id !== id));
  };

  const handleUpdateSubItem = (id: string, updates: Partial<SubItemForm>) => {
    onSubItemsChange(
      subItems.map((item) =>
        item.id === id ? { ...item, ...updates } : item
      ) as SubItemForm[]
    );
  };

  return (
    <div className="space-y-4">
      {subItems.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">
          No cost items yet. Use + Add item to get started.
        </p>
      ) : (
        <div className="space-y-1">
          {subItems.map((item) =>
            isQtyRateType(item.type, typeMeta) ? (
              <LaborSubItemRow
                key={item.id}
                item={item as LaborSubItemForm}
                isEditingNote={editingNoteIds.has(item.id)}
                onUpdate={(updates) => handleUpdateSubItem(item.id, updates)}
                onDelete={() => handleRemoveSubItem(item.id)}
                onStartEditingNote={() => startEditingNote(item.id)}
                onStopEditingNote={() => stopEditingNote(item.id)}
                readOnly={readOnly}
                jobMarkupRate={markupRate}
                label={getSubItemLabel(item.type, typeMeta)}
                category={item.type}
                isSelected={selectedSubItemId === item.id}
                onClick={() => onSelectSubItem?.(item.id)}
              />
            ) : (
              <DirectSubItemRow
                key={item.id}
                item={item as DirectSubItemForm}
                isEditingNote={editingNoteIds.has(item.id)}
                onUpdate={(updates) => handleUpdateSubItem(item.id, updates)}
                onDelete={() => handleRemoveSubItem(item.id)}
                onStartEditingNote={() => startEditingNote(item.id)}
                onStopEditingNote={() => stopEditingNote(item.id)}
                readOnly={readOnly}
                jobMarkupRate={markupRate}
                isSelected={selectedSubItemId === item.id}
                onClick={() => onSelectSubItem?.(item.id)}
              />
            )
          )}
        </div>
      )}

      {subItems.length > 0 && (
        <CostBreakdownSummary
          breakdown={breakdown}
          taxRate={taxRate}
          markupRate={markupRate}
          onRemoveRounding={onRemoveRounding}
        />
      )}
    </div>
  );
}
